/**
 * Flagship Motion — boot sequence.
 *
 * Runs after animation-utils.js. Waits for GSAP + plugins to load (they're
 * deferred), then:
 *   1. Registers GSAP plugins
 *   2. Handles Shopify theme editor section lifecycle events
 *   3. Refreshes ScrollTriggers on font + image load
 *
 * NOTE: Smooth scroll (Lenis) was removed from the boot path — we use native
 * browser scroll for all ScrollTriggers now. See comment near the old Lenis
 * block below for how to re-enable if a specific scene needs it later.
 */
(function () {
  'use strict';

  function boot() {
    var F = window.FlagshipMotion;
    if (!F) return;

    // Wait for GSAP + ScrollTrigger to be ready (they load with `defer`)
    F.onReady(function () {
      // --- Register plugins ---
      window.gsap.registerPlugin(window.ScrollTrigger);
      if (window.SplitText) {
        window.gsap.registerPlugin(window.SplitText);
      }

      // ─── MOBILE CHROME BAR DEFENSE ──────────────────────────────────
      // ignoreMobileResize: prevents ScrollTrigger from refreshing
      // on chrome-bar resize events globally.
      //
      // normalizeScroll(true) is NOT enabled globally — it locks iOS
      // chrome in its initial state which causes chrome to stick
      // visible after sections that triggered it to show. Instead,
      // panel-stack's own JS toggles normalizeScroll on while it's
      // in view and off when out of view (see its IntersectionObserver).
      // Other sections rely on iOS native chrome behavior.
      window.ScrollTrigger.config({ ignoreMobileResize: true });

      // --- Theme editor safety: add a class to <html> so sections can
      //     opt out of heavy animations in design mode if they want ---
      if (F.isEditor) {
        document.documentElement.classList.add('flagship-is-editor');
      }

      // --- Smooth scroll: DISABLED ---
      // Lenis was removed from the active boot path because it fights the site
      // on pages that don't benefit from it, and it made testing every other
      // scene unnecessarily hard. ScrollTrigger now uses native window scroll,
      // which is the default and needs no proxy wiring.
      //
      // To re-enable later for a specific scene:
      //   1. Uncomment the Lenis <script> tag in layout/theme.liquid
      //   2. Restore a gated Lenis boot block here (reference git history)
      //   3. Prefer a LOCAL smooth-scroll (scroll-linked tween inside a pinned
      //      section) over a global scroll hijack — global always feels wrong
      //      on content-heavy pages (product, cart, account).
      //
      // See tasks/todo.md "Rip out Lenis" for context.

      // --- Refresh triggers after fonts load (line-height changes move things) ---
      F.waitForFonts().then(function () {
        F.refreshTriggers();
      });

      // --- Refresh on image load (Shopify lazy-loaded images can shift layout) ---
      window.addEventListener('load', function () {
        F.refreshTriggers();
      });

      // --- Shopify theme editor: re-init triggers when sections are added/
      //     removed/re-ordered. Each flagship-* section listens for its own
      //     `shopify:section:load` — this global handler just refreshes. ---
      document.addEventListener('shopify:section:load', function () {
        // Let the section's own init run first, then refresh
        requestAnimationFrame(function () { F.refreshTriggers(); });
      });

      document.addEventListener('shopify:section:unload', function (e) {
        // Kill any ScrollTriggers whose trigger element lived inside the
        // unloaded section — prevents orphan triggers polluting the page
        if (!window.ScrollTrigger) return;
        var unloadedSection = e.target;
        window.ScrollTrigger.getAll().forEach(function (st) {
          if (st.trigger && unloadedSection.contains(st.trigger)) {
            st.kill();
          }
        });
        F.refreshTriggers();
      });

      // --- Mark motion as booted so sections waiting for us can start ---
      document.documentElement.classList.add('flagship-motion-ready');
      window.dispatchEvent(new CustomEvent('flagship:ready'));
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }
})();
